import React, { useState } from 'react';
import './DonationWidget.css';
import { apiService, CreateDonationRequest, PayFastData } from '../../../services/api.service';
import { createPayFastFormData, submitToPayFast, PayFastConfig } from '../../../utils/payfast';

interface DonationWidgetProps {
  onSuccess: (amount: number) => void;
  onError: (error: string) => void;
}

const DonationWidget: React.FC<DonationWidgetProps> = ({ onSuccess, onError }) => {
  const [activeTab, setActiveTab] = useState<'one-time' | 'monthly'>('one-time');
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedAmount, setSelectedAmount] = useState<number>(10);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    cellNumber: ''
  });
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const predefinedAmounts = [10, 25, 50, 100];

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (value: string) => {
    setCustomAmount(value);
    if (value) {
      setSelectedAmount(parseFloat(value) || 0);
    }
  };

  const handleNext = () => {
    if (step === 1) {
      setStep(2);
    }
  };

  const handlePrevious = () => {
    if (step === 2) {
      setStep(1);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = (): boolean => {
    const { firstName, lastName, email, cellNumber } = formData;
    
    if (!firstName.trim()) {
      onError('Please enter your first name');
      return false;
    }
    
    if (!lastName.trim()) {
      onError('Please enter your last name');
      return false;
    }
    
    if (!email.trim()) {
      onError('Please enter your email address');
      return false;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      onError('Please enter a valid email address');
      return false;
    }
    
    if (!cellNumber.trim()) {
      onError('Please enter your cell number');
      return false;
    }
    
    if (selectedAmount <= 0) {
      onError('Please select a valid donation amount');
      return false;
    }
    
    return true;
  };

  const getClientInfo = () => {
    return {
      ipAddress: '', // This would typically be obtained from the server
      userAgent: navigator.userAgent,
      campaign: new URLSearchParams(window.location.search).get('campaign') || undefined
    };
  };

  const handleDonate = async () => {
    if (!validateForm()) {
      return;
    }

    setIsProcessing(true);

    try {
      // Prepare donation data
      const clientInfo = getClientInfo();
      const donationData: CreateDonationRequest = {
        amount: selectedAmount,
        donationType: activeTab === 'monthly' ? 'monthly' : 'one-time',
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        cellNumber: formData.cellNumber.trim(),
        campaign: clientInfo.campaign
      };

      // Create donation record via API
      console.log('Creating donation record...', donationData);
      const response = await apiService.createDonation(donationData);
      
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to create donation record');
      }

      const donation = response.data;
      console.log('Donation created successfully:', donation.donationId);

      // Prepare PayFast data - simplified to match your working sandbox example
      const config = (window as any).APP_CONFIG || {};
      const payfastData = {
        merchant_id: config.PAYFAST_MERCHANT_ID || '10000100',
        merchant_key: config.PAYFAST_MERCHANT_KEY || '46f0cd694581a',
        return_url: `${window.location.origin}/donation-success?donation_id=${donation.donationId}`,
        cancel_url: `${window.location.origin}/donation-cancelled?donation_id=${donation.donationId}`,
        notify_url: `${window.location.origin}/api/payfast/notify`,
        amount: selectedAmount,
        item_name: 'Donation',
        m_payment_id: donation.donationId, // Pass our MongoDB donation ID as reference
        custom_str1: donation.donationId   // Also pass it in custom field for extra tracking
      };

      // Update donation with PayFast data
      const updateResponse = await apiService.updateWithPayFastData(donation.donationId, payfastData);
      
      if (!updateResponse.success) {
        console.warn('Failed to update PayFast data:', updateResponse.message);
        // Continue anyway - this is not critical for the donation flow
      }

      // Submit to PayFast sandbox (no signature needed for sandbox)
      const isSandbox = config.PAYFAST_SANDBOX !== 'false';
      console.log('Submitting to PayFast sandbox (no signature):', payfastData);
      
      // Submit directly to PayFast - no signature for sandbox
      submitToPayFast(payfastData, isSandbox);

    } catch (error) {
      console.error('Error processing donation:', error);
      onError(error instanceof Error ? error.message : 'Failed to process donation. Please try again.');
      setIsProcessing(false);
    }
  };

  const getButtonText = () => {
    if (isProcessing) {
      return 'PROCESSING...';
    }
    const amount = selectedAmount;
    const prefix = activeTab === 'monthly' ? 'DONATE R' : 'DONATE R';
    const suffix = activeTab === 'monthly' ? ' MONTHLY' : '';
    return `${prefix}${amount}${suffix}`;
  };

  return (
    <div className="donation-widget">
      {/* Tabs - Only show on step 1 */}
      {step === 1 && (
        <div className="donation-tabs">
          <button
            className={`tab ${activeTab === 'one-time' ? 'active' : ''}`}
            onClick={() => setActiveTab('one-time')}
          >
            ONE-TIME DONATION
          </button>
          <button
            className={`tab ${activeTab === 'monthly' ? 'active' : ''}`}
            onClick={() => setActiveTab('monthly')}
          >
            MONTHLY DONATION
          </button>
        </div>
      )}

      {/* Content */}
      <div className="donation-content">
        {step === 1 ? (
          <>
            {/* Step 1: Amount Selection */}
            <div className="donation-step">
              <h2 className="donation-title">
                {activeTab === 'one-time' ? 'Make a One-Time Difference' : 'Make a Monthly Difference'}
              </h2>
              
              <p className="donation-description">
                {activeTab === 'one-time' 
                  ? 'If you prefer making a single, impactful contribution, our One-Time Donation option is the perfect choice. Every donation counts and makes an immediate difference in our mission to eliminate malaria in southern Africa.'
                  : 'When it comes to battling malaria, consistency is key. Join our Monthly Support and make a lasting impact. Your regular contributions help us maintain our life-saving efforts throughout the year. By pledging a monthly donation, you become a vital part of our mission to defeat malaria in southern Africa.'
                }
              </p>

              <div className="amount-selection">
                <div className="amount-buttons">
                  {predefinedAmounts.map((amount) => (
                    <button
                      key={amount}
                      className={`amount-btn ${selectedAmount === amount && !customAmount ? 'selected' : ''}`}
                      onClick={() => handleAmountSelect(amount)}
                    >
                      Donate R{amount}{activeTab === 'monthly' ? ' Monthly' : ''}
                    </button>
                  ))}
                </div>

                <div className="custom-amount">
                  <label>OTHER AMOUNT</label>
                  <input
                    type="number"
                    placeholder="Custom Amount"
                    value={customAmount}
                    onChange={(e) => handleCustomAmountChange(e.target.value)}
                  />
                </div>
              </div>

              <button className="donate-btn" onClick={handleNext}>
                {getButtonText()}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Step 2: Personal Information */}
            <div className="donation-step">
              <h2 className="donation-title">Support Our Cause – Make a Donation Today</h2>
              
              <p className="donation-description">
                Thank you for choosing to make a difference through your generous donation. Your support means the world to us and is a vital step in our mission to eliminate malaria in southern Africa. Please provide your information below to complete your donation securely.
              </p>

              <div className="form-section">
                <h3 className="form-title">PLEASE COMPLETE THE BELOW INFORMATION TO MAKE YOUR DONATION SECURELY.</h3>
                
                <div className="form-fields">
                  <div className="form-field">
                    <label>First Name</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                    />
                  </div>

                  <div className="form-field">
                    <label>Last Name</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                    />
                  </div>

                  <div className="form-field">
                    <label>Your Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                    />
                  </div>

                  <div className="form-field">
                    <label>Cell Number</label>
                    <input
                      type="tel"
                      value={formData.cellNumber}
                      onChange={(e) => handleInputChange('cellNumber', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-buttons">
                  <button className="previous-btn" onClick={handlePrevious} disabled={isProcessing}>
                    PREVIOUS
                  </button>
                  <button className="donate-btn" onClick={handleDonate} disabled={isProcessing}>
                    {isProcessing ? 'PROCESSING...' : 'DONATE NOW'}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DonationWidget;
